<script setup lang="ts">
import { ref } from 'vue';
import { format, parseISO } from 'date-fns';
import { Download } from 'lucide-vue-next';
import { useToast } from 'vue-toastification';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import { drugUnitValue, formatQuantity, formatSignedPercent, PERCENT_TONE_CLASSES, percentTone } from '@/composables/inventory/inventoryTable';
import { quantityMetricOf, REPORT_METRIC_IDS, type ReportMetricId } from '@/composables/inventory/inventoryReport';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineReportColumn, MedicineReportFarm } from '@/types';
import { downloadBlob } from '@/util/downloadFile';
import { buildInventoryReportWorkbook } from '@/util/inventoryReportWorkbook';

const props = defineProps<{ farms: MedicineReportFarm[] }>();

defineEmits<{ reset: [] }>();

const toast = useToast();
const isExporting = ref(false);

const t = (key: string) => i18n.global.t(`views.health.inventory.report.${key}`);

const metricLabel = (metric: ReportMetricId) => t(`metrics.${metric}`);

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function periodOf(farm: MedicineReportFarm): string {
    return t('period').replace('%START%', formatDate(farm.start_date)).replace('%END%', formatDate(farm.end_date));
}

function columnLabel(column: MedicineReportColumn): string {
    return `${column.drug_name} (${column.unit_name})`;
}

/** A quantity metric of a column, or null for a figure that was not counted. */
function quantityOf(column: MedicineReportColumn, metric: ReportMetricId): number | null {
    return metric === 'deviation' ? null : quantityMetricOf(column, metric);
}

async function downloadExcel() {
    isExporting.value = true;

    try {
        const blob = await buildInventoryReportWorkbook({
            farms: props.farms,
            metricLabels: Object.fromEntries(REPORT_METRIC_IDS.map((metric) => [metric, metricLabel(metric)])) as Record<ReportMetricId, string>,
            metricHeader: t('metricHeader'),
            periodLabel: periodOf,
            columnLabel,
        });

        downloadBlob(blob, `stock-statement_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    } catch {
        // Without this the workbook silently fails to appear and the click looks dead.
        toast.error(t('exportError'));
    } finally {
        isExporting.value = false;
    }
}
</script>

<template>
    <div class="space-y-6">
        <div class="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" @click="$emit('reset')">{{ $t('views.health.inventory.report.startOver') }}</Button>
            <Button :disabled="isExporting" @click="downloadExcel">
                <Spinner v-if="isExporting" class="size-4 animate-spin" aria-hidden="true" />
                <Download v-else class="size-4" aria-hidden="true" />
                {{ $t('views.health.inventory.report.downloadExcel') }}
            </Button>
        </div>

        <MainViewCard v-for="farm in farms" :key="farm.farm_id" class="overflow-hidden">
            <div class="px-4 pt-4 pb-2">
                <h2 class="text-lg font-medium">{{ farm.farm_name }}</h2>
                <div class="text-gray-500">{{ periodOf(farm) }}</div>
            </div>
            <div v-if="farm.columns.length === 0" class="px-4 py-10 text-center text-gray-500">{{ $t('views.health.inventory.report.noColumns') }}</div>
            <div v-else class="overflow-x-auto">
                <table class="w-full text-normal">
                    <thead>
                        <tr class="border-y border-gray-200 bg-gray-50 text-gray-500">
                            <th class="sticky left-0 z-10 bg-gray-50 px-4 py-2 text-left font-medium">{{ $t('views.health.inventory.report.metricHeader') }}</th>
                            <th v-for="column in farm.columns" :key="drugUnitValue(column)" class="px-4 py-2 text-right font-medium">
                                <div class="text-gray-900">{{ column.drug_name }}</div>
                                <div class="font-normal">{{ column.unit_name }}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="metric in REPORT_METRIC_IDS" :key="metric" class="border-b border-gray-100 last:border-b-0">
                            <th class="sticky left-0 z-10 bg-white px-4 py-2 text-left font-medium whitespace-nowrap">{{ metricLabel(metric) }}</th>
                            <td v-for="column in farm.columns" :key="drugUnitValue(column)" class="px-4 py-2 text-right whitespace-nowrap">
                                <template v-if="metric === 'deviation'">
                                    <Badge v-if="column.deviation_percent !== null" variant="outline" :class="PERCENT_TONE_CLASSES[percentTone(Math.abs(column.deviation_percent))]">
                                        {{ formatSignedPercent(column.deviation_percent) }}
                                    </Badge>
                                    <span v-else class="text-gray-400" aria-hidden="true">-</span>
                                </template>
                                <template v-else>
                                    <span v-if="quantityOf(column, metric) !== null" :class="{ 'font-semibold': metric === 'end' }">{{ formatQuantity(quantityOf(column, metric)!) }}</span>
                                    <span v-else class="text-gray-400" aria-hidden="true">-</span>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </MainViewCard>
    </div>
</template>
